import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLatestPrograms } from '../store/reducers/programs';
import { AppDispatch, RootState } from '../store';
import CoachMark from '../interfaces/CoachMark';
import DashboardCoachMark from '../components/coach-mark/Dashboard';
import { User } from '../models/User';
import CalendarCoachMark from '../components/coach-mark/Calendar';
import HistoryCoachMark from '../components/coach-mark/History';
import UpcomingCoachMark from '../components/coach-mark/Upcoming';
import Greeting from '../components/shared/Greeting';
import UpcomingPanel from '../components/panels/Upcoming';
import OverviewWidget from '../widgets/overview/Overview';
import PendingTaskPanel from '../components/panels/PendingTask';
import { Link, Outlet } from 'react-router-dom';
import LatestProgrammes from '../components/program/components/LatestProgrammes';
import LatestProgrammmesCard from '../components/program/components/LatestProgrammmesCard';
import CoachMarks from '../components/coach-mark/CoachMarks';
import { getProfileData } from 'store/reducers/authentication';
import TodayEventCard from 'components/calendar/TodayEventCard';
import IEvent from 'models/Event';
import { fetchUpcomingEvents } from 'store/reducers/event';

interface Props {
  events: IEvent[];
}

type ScheduledType = "Past" | "Today" | "Upcoming"



const Dashboard = () => {
  const [user, setUser] = useState<User>()
  const userState = useSelector((state: RootState) => state.auth.user as User);
  const { latestPrograms } = useSelector((state: RootState) => state.programs)
  const { items, isLoading } = useSelector((state: RootState) => state.events)
  const [scheduledType, setScheduledType] = useState<ScheduledType>("Today");
  const scheduledTypes: ScheduledType[] = ["Past", "Today", "Upcoming"];



  useEffect(() => {
    setUser(userState);
  }, [userState])


  const coachMarks: CoachMark[] = [
    {
      heading: "Dashboard",
      targetId: "dashboard-menu",
      content: <DashboardCoachMark />,
      headerAlignmentType: "top",
    },
    {
      heading: "Calendar",
      targetId: "calendar-menu",
      content: <CalendarCoachMark />,
      headerAlignmentType: "top",
    },
    {
      heading: "History",
      targetId: "history-menu",
      content: <HistoryCoachMark />,
      headerAlignmentType: "top",
    },
    {
      heading: "Upcoming Programmes",
      targetId: "upcoming-programme",
      content: <UpcomingCoachMark />,
      headerAlignmentType: "right",
    },
    // Add more coach marks as needed.
  ];

  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    dispatch(getLatestPrograms());
    dispatch(getProfileData());
    dispatch(fetchUpcomingEvents())

  }, []);

  const filterEvents = (filterType: ScheduledType) => {
    const currentDate = new Date();
    const todayStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000); // End of today




    switch (filterType) {
      // case "Past":
      //   return events.filter((event) => new Date(event.date) < todayStart);
      case "Today":
        return items.filter(
          (event) =>
            new Date(event.date).getDate() === currentDate.getDate() &&
            new Date(event.date).getMonth() === currentDate.getMonth() &&
            new Date(event.date).getFullYear() === currentDate.getFullYear()
        );
      // case "Upcoming":
      //   return events.filter((event) => new Date(event.date) > todayEnd);
      default:
        return items;
    }
  };

  const filteredEvents = filterEvents(scheduledType);
  const groupedEvents: { [monthYear: string]: { [date: string]: IEvent[] } } = {};
  filteredEvents.forEach((event) => {

    const monthYear = `${new Date(event.date).toLocaleString("default", { month: "long" })} ${new Date(event.date).getFullYear()}`;
    const date = `${new Date(event.date).getDate()}-${new Date(event.date).toLocaleString("default", { month: "short" })}-${new Date(event.date).getFullYear()}`;
    if (!groupedEvents[monthYear]) {
      groupedEvents[monthYear] = {};
    }
    if (!groupedEvents[monthYear][date]) {
      groupedEvents[monthYear][date] = [];
    }
    groupedEvents[monthYear][date].push(event);
  });

  return (
    <>
      <div className="grid grid-cols-4 w-full h-full mobile:grid-cols-1 gap-4 justify-center">
        <div className="col-span-3 mobile:col-span-4 row-span-1">
          {user?.name && <Greeting name={user?.name}>Welcome to your dashboard!</Greeting>}
        </div>
        <div
          id="target-1"
          className="col-span-1 row-span-4 mobile:hidden tablet:hidden"
        >
          <UpcomingPanel />
        </div>
        <div className="block tablet:hidden laptop:hidden desktop:hidden largescreen:hidden xxl:hidden ml-4">
          {Object.keys(groupedEvents).map((monthYear) => (
            <div key={monthYear}>
              {Object.keys(groupedEvents[monthYear]).map((date) => (
                <div key={date}>
                  {groupedEvents[monthYear][date].map((event) => (
                    <TodayEventCard key={event.id} event={event} />
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="flex col-span-2 mobile:col-span-4">
          <OverviewWidget />
        </div>
        <div className="col-span-1 mobile:col-span-4 tablet:col-span-2">
          <PendingTaskPanel />
        </div>
        <div className="flex flex-col col-span-3 tablet:col-span-4 laptop:col-span-3 desktop:col-span-3 largescreen:col-span-3">
          <div className="flex flex-row text-app-blue font-semibold text-sm mb-1 justify-between">
            <div className="flex justify-start text-sm">Latest Programmes</div>
            {latestPrograms.length > 5 && (
              <div className="flex justify-end text-sm">
                <Link to="/view-all-programs" className="hover:cursor-pointer text-app-blue text-sm font-semibold">
                  View All Programmes
                </Link>
              </div>
            )}
          </div>
          <div>
            <hr className="mb-4 border-app-blue border-[1.5px] w-full" />
          </div>
          <div className="flex mobile:hidden tablet:hidden flex-grow">
            <LatestProgrammes data={latestPrograms} limit={5} />
          </div>
          <div className="hidden mobile:flex mobile:justify-stretch mb-10 tablet:flex">
            <LatestProgrammmesCard data={latestPrograms} limit={5} />
          </div>
        </div>
      </div>
      <CoachMarks marks={coachMarks} />
      {/* <div className="w-full bg-app-blue py-2 text-center text-xs mobile:text-sm tablet:py-3 tablet:text-base laptop:py-4 laptop:text-lg desktop:text-xl largescreen:text-2xl xxl:text-3xl text-white mt-2 mobile:mb-8 tablet:mt-4 laptop:mt-5 desktop:mt-6 largescreen:mt-8 xxl:mt-10">
        For better experience, download the app from{" "}
        <a
          href=""
          className="text-app-blue hover:underline font-semibold"
          target="_blank"
          rel="noopener noreferrer"
        >
          here
        </a>
      </div> */}
    </>
  );
};

export default Dashboard;